import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api.js';
import ConfirmModal from './ConfirmModal';
import NoticeModal from './NoticeModal';

const AccountsPage = ({ panelId }) => {
    // İzleyici rolü yalnızca görüntüleme yapar; değişiklik butonları gizlenir.
    const _role = (JSON.parse(localStorage.getItem('user') || '{}').role);
    const isViewer = _role === 'izleyici';
    const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null, danger: false });
    const askConfirm = (message, onConfirm, danger = false) => setConfirmState({ open: true, message, onConfirm, danger });
    const closeConfirm = () => setConfirmState({ open: false, message: '', onConfirm: null, danger: false });
    const [notice, setNotice] = useState({ open: false, message: '', danger: false });
    const showNotice = (message, danger = false) => setNotice({ open: true, message, danger });
    const [accounts, setAccounts] = useState([]);
    const [banks, setBanks] = useState([]);

    // Filtreleme ve Sıralama State'leri
    const [filters, setFilters] = useState({
        bankName: '',
        accountHolder: '',
        iban: '',
        remainingLimit: '',
        budgetRange: ''
    });
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    // Edit Modal State
    const [editingAccount, setEditingAccount] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Create Modal State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newAccountData, setNewAccountData] = useState({
        bankName: '',
        iban: '',
        accountHolder: '',
        budgetMin: '',
        budgetMax: '',
        remainingLimit: '',
        fastCountLimit: '',
        fastAmountLimit: '',
        isActive: true
    });

    const fetchAccounts = useCallback(async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            const res = await api.getAccounts({ panelId: activePanelId || '' });
            setAccounts(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [panelId]);

    const fetchBanks = async () => {
        try {
            const res = await api.getBanks();
            setBanks(res.data);
        } catch (error) {
            console.error("Bankalar yüklenemedi", error);
        }
    };

    useEffect(() => {
        document.title = 'PayzoPay - Hesaplar';
        fetchAccounts();
        fetchBanks();
    }, [fetchAccounts]);

    const logAction = async (action, details) => {
        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || user?.panelId;
            const username = user ? user.username : 'Bilinmiyor';
            await api.createLog({ username, action, details, panelId: activePanelId });
        } catch (error) {
            console.error('Loglama hatası:', error);
        }
    };

    // Filtreleme ve Sıralama Mantığı
    const processedAccounts = useMemo(() => {
        let result = [...accounts];

        // 1. Filtreleme
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                const filterVal = filters[key].toLowerCase();
                result = result.filter(acc => {
                    let val = acc[key];
                    // Özel alan kontrolleri
                    if (key === 'budgetRange' && Array.isArray(val)) {
                        val = val.join(' - ');
                    }
                    return String(val || '').toLowerCase().includes(filterVal);
                });
            }
        });

        // 2. Sıralama
        if (sortConfig.key) {
            result.sort((a, b) => {
                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                // Bütçe aralığı için ilk değere (Min) göre sırala
                if (sortConfig.key === 'budgetRange' && Array.isArray(valA)) valA = valA[0];
                if (sortConfig.key === 'budgetRange' && Array.isArray(valB)) valB = valB[0];

                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return result;
    }, [accounts, filters, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const toggleStatus = async (account) => {
        try {
            await api.updateAccount(account._id, { isActive: !account.isActive });
            fetchAccounts();
            logAction('Hesap Durum Değişikliği', `${account.bankName} hesabı ${!account.isActive ? 'aktif' : 'pasif'} yapıldı.`);
        } catch (error) {
            showNotice('Durum güncellenemedi.', true);
        }
    };

    const handleDelete = (account) => {
        askConfirm(`"${account.bankName} - ${account.accountHolder}" hesabını silmek istediğinize emin misiniz?`, async () => {
            try {
                await api.deleteAccount(account._id);
                fetchAccounts();
                logAction('Hesap Silme', `${account.bankName} hesabı silindi.`);
            } catch (error) {
                showNotice('Silme başarısız: ' + (error.response?.data?.error || error.message), true);
            }
        }, true);
    };

    const handleEditClick = (account) => {
        setEditingAccount({
            ...account,
            budgetMin: account.budgetRange ? account.budgetRange[0] : '',
            budgetMax: account.budgetRange ? account.budgetRange[1] : '',
            remainingLimit: account.remainingLimit || ''
        });
        setShowModal(true);
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditingAccount(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...editingAccount,
                budgetRange: [Number(editingAccount.budgetMin), Number(editingAccount.budgetMax)]
            };
            await api.updateAccount(editingAccount._id, payload);
            setShowModal(false);
            setEditingAccount(null);
            fetchAccounts();
            logAction('Hesap Düzenleme', `${editingAccount.bankName} hesabı güncellendi.`);
        } catch (error) {
            showNotice('Güncelleme hatası.', true);
        }
    };

    const handleCreateChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewAccountData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            const activePanelId = panelId || currentUser?.panelId;
            const payload = {
                ...newAccountData,
                panelId: activePanelId,
                budgetRange: [Number(newAccountData.budgetMin), Number(newAccountData.budgetMax)]
            };
            await api.createAccount(payload);
            logAction('Hesap Oluşturma', `${newAccountData.bankName} hesabı oluşturuldu.`);
            setShowCreateModal(false);
            setNewAccountData({
                bankName: '', iban: '', accountHolder: '', budgetMin: '', budgetMax: '', remainingLimit: '', fastCountLimit: '', fastAmountLimit: '', isActive: true
            });
            fetchAccounts();
        } catch (error) {
            console.error(error);
            showNotice('Kaydedilirken bir hata oluştu.', true);
        }
    };

    const renderHeader = (label, key) => (
        <th style={{ minWidth: '120px' }}>
            <div
                onClick={() => requestSort(key)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
                {label}
                <span>
                    {sortConfig.key === key ? (sortConfig.direction === 'ascending' ? ' ▲' : ' ▼') : ' ⇅'}
                </span>
            </div>
            <input
                type="text"
                value={filters[key] || ''}
                onChange={e => handleFilterChange(key, e.target.value)}
                placeholder="Filtrele..."
                style={{ width: '100%', marginTop: '5px', padding: '4px', boxSizing: 'border-box', fontSize: '0.9em', color: 'var(--text-color)', backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
            />
        </th>
    );

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ color: '#fff', margin: 0 }}>Hesaplar</h2>
                {!isViewer && (
                <button
                    onClick={() => setShowCreateModal(true)}
                    style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                    + Hesap Tanımlama
                </button>
                )}
            </div>

            {/* Tablo */}
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '15px', boxShadow: 'var(--shadow)', overflow: 'hidden', padding: '20px' }}>
                <table cellPadding="15" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px', color: 'var(--text-color)' }}>
                    <thead>
                        <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                            {renderHeader('Banka', 'bankName')}
                            {renderHeader('Hesap Sahibi', 'accountHolder')}
                            {renderHeader('IBAN', 'iban')}
                            {renderHeader('Adet Limiti', 'fastCountLimit')}
                            {renderHeader('Max-Min', 'budgetRange')}
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {processedAccounts.map(acc => {
                            const rowBg = acc.isActive ? 'rgba(160, 212, 104, 0.5)' : 'rgba(237, 85, 101, 0.5)';
                            const radius = '15px';
                            return (
                                <tr key={acc._id}>
                                    <td style={{ backgroundColor: rowBg, borderTopLeftRadius: radius, borderBottomLeftRadius: radius }}>{acc.bankName}</td>
                                    <td style={{ backgroundColor: rowBg }}>{acc.accountHolder}</td>
                                    <td style={{ backgroundColor: rowBg }}>{acc.iban}</td>
                                    <td style={{ backgroundColor: rowBg }}>{(() => {
                                        const limit = Number(acc.fastCountLimit) || 0;
                                        const used = Number(acc.fastUsedCount) || 0;
                                        return limit > 0 ? `${used}/${limit} adet` : 'Limitsiz';
                                    })()}</td>
                                    <td style={{ backgroundColor: rowBg }}>{acc.budgetRange ? `${acc.budgetRange[0]} - ${acc.budgetRange[1]}` : '-'}</td>
                                    <td style={{ backgroundColor: rowBg }}>
                                        <span style={{
                                            padding: '10px 20px',
                                            borderRadius: '10px',
                                            background: acc.isActive ? 'green' : 'red',
                                            color: 'white',
                                            fontSize: '1.0em'
                                        }}>
                                            {acc.isActive ? 'Aktif' : 'Pasif'}
                                        </span>
                                    </td>
                                    <td style={{ backgroundColor: rowBg, borderTopRightRadius: radius, borderBottomRightRadius: radius }}>
                                        {!isViewer && (
                                        <>
                                        <button onClick={() => handleEditClick(acc)} style={{ marginRight: '5px', cursor: 'pointer', padding: '5px 10px', border: 'none', backgroundColor: 'transparent', fontSize: '1.2em' }} title="Düzenle">
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button onClick={() => toggleStatus(acc)} style={{ cursor: 'pointer', padding: '5px 10px', border: 'none', backgroundColor: 'transparent', fontSize: '1.2em' }} title={acc.isActive ? 'Pasife Al' : 'Aktif Et'}>
                                            {acc.isActive ? <i className="fas fa-times" style={{ color: '#dc3545' }}></i> : <i className="fas fa-check" style={{ color: '#28a745' }}></i>}
                                        </button>
                                        <button onClick={() => handleDelete(acc)} style={{ cursor: 'pointer', padding: '5px 10px', border: 'none', backgroundColor: 'transparent', fontSize: '1.2em' }} title="Sil">
                                            <i className="fas fa-trash" style={{ color: '#dc3545' }}></i>
                                        </button>
                                        </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Edit Modal */}
            {showModal && editingAccount && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', color: 'var(--text-color)' }}>
                        <h3>Hesabı Düzenle</h3>
                        <form onSubmit={handleEditSubmit}>
                            <div style={{ marginBottom: '10px' }}>
                                <label>Banka:</label>
                                <input name="bankName" value={editingAccount.bankName} onChange={handleEditChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label>Hesap Sahibi:</label>
                                <input name="accountHolder" value={editingAccount.accountHolder} onChange={handleEditChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                            </div>
                            <div style={{ marginBottom: '10px' }}>
                                <label>IBAN:</label>
                                <input name="iban" value={editingAccount.iban} onChange={handleEditChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                            </div>
                            <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <label>Min Bütçe:</label>
                                    <input type="number" name="budgetMin" value={editingAccount.budgetMin} onChange={handleEditChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Max Bütçe:</label>
                                    <input type="number" name="budgetMax" value={editingAccount.budgetMax} onChange={handleEditChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                </div>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <label>Adet Limiti (0 = limitsiz):</label>
                                <input type="number" name="fastCountLimit" value={editingAccount.fastCountLimit || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                <small style={{ opacity: 0.7 }}>Kullanılan: {editingAccount.fastUsedCount || 0} adet</small>
                            </div>
                            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{ marginRight: '10px', padding: '8px 16px', cursor: 'pointer' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: 'blue', color: 'white', border: 'none', cursor: 'pointer' }}>Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '30px', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', color: 'var(--text-color)' }}>
                        <h3>Yeni Hesap Tanımla</h3>
                        <form onSubmit={handleCreateSubmit}>
                            <div style={{ marginBottom: '10px' }}>
                                <select name="bankName" value={newAccountData.bankName} onChange={handleCreateChange} required style={{ width: '100%', padding: '8px', marginBottom: '10px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }}>
                                    <option value="">Banka Seçiniz</option>
                                    {banks.map(bank => (
                                        <option key={bank._id} value={bank.displayName}>{bank.displayName}</option>
                                    ))}
                                </select>
                                <input name="iban" placeholder="IBAN" value={newAccountData.iban} onChange={handleCreateChange} required style={{ width: '100%', padding: '8px', marginBottom: '10px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                <input name="accountHolder" placeholder="Hesap Sahibi" value={newAccountData.accountHolder} onChange={handleCreateChange} required style={{ width: '100%', padding: '8px', marginBottom: '10px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />

                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <input type="number" name="budgetMin" placeholder="Min Bütçe" value={newAccountData.budgetMin} onChange={handleCreateChange} required style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <input type="number" name="budgetMax" placeholder="Max Bütçe" value={newAccountData.budgetMax} onChange={handleCreateChange} required style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                    </div>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <input type="number" name="fastCountLimit" placeholder="Adet Limiti (0 = limitsiz)" value={newAccountData.fastCountLimit} onChange={handleCreateChange} style={{ width: '100%', padding: '8px', boxSizing: 'border-box', backgroundColor: 'var(--input-bg)', color: 'var(--text-color)', border: '1px solid var(--input-border)' }} />
                                </div>

                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', cursor: 'pointer' }}>
                                    <input type="checkbox" name="isActive" checked={newAccountData.isActive} onChange={handleCreateChange} />
                                    Aktif mi?
                                </label>
                            </div>

                            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    style={{ marginRight: '10px', padding: '8px 16px', cursor: 'pointer', background: '#ccc', border: 'none', borderRadius: '4px' }}
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                >
                                    Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={confirmState.open}
                message={confirmState.message}
                danger={confirmState.danger}
                onConfirm={() => { if (confirmState.onConfirm) confirmState.onConfirm(); closeConfirm(); }}
                onCancel={closeConfirm}
            />

            <NoticeModal
                open={notice.open}
                message={notice.message}
                danger={notice.danger}
                onClose={() => setNotice({ open: false, message: '', danger: false })}
            />
        </div>
    );
};

export default AccountsPage;